import { useEffect, useRef, useCallback } from 'react';

interface TouchSwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  minDistance?: number; // Minimum distance in pixels (default: 50)
  minVelocity?: number; // Minimum velocity in px/ms (default: 0.3)
  distanceThreshold?: number; // Distance threshold as percentage of screen width (default: 0.3 = 30%)
  enabled?: boolean; // Enable/disable swipe detection
  element?: HTMLElement | null; // Element to attach listeners to (default: document.body)
}

interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
  currentX: number;
  currentY: number;
  isSwiping: boolean;
}

export function useTouchSwipe(options: TouchSwipeOptions) {
  const {
    onSwipeLeft,
    onSwipeRight,
    minDistance = 50,
    minVelocity = 0.3,
    distanceThreshold = 0.345, // Increased by 15% from 0.3
    enabled = true,
    element,
  } = options;

  const touchState = useRef<TouchState>({
    startX: 0,
    startY: 0,
    startTime: 0,
    currentX: 0,
    currentY: 0,
    isSwiping: false,
  });

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;

      // Only work on touch devices
      if (!('ontouchstart' in window)) return;

      const touch = e.touches[0];
      if (!touch) return;

      touchState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        currentX: touch.clientX,
        currentY: touch.clientY,
        isSwiping: false,
      };
    },
    [enabled]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;
      if (!touchState.current.startTime) return;

      const touch = e.touches[0];
      if (!touch) return;

      touchState.current.currentX = touch.clientX;
      touchState.current.currentY = touch.clientY;

      const deltaX = Math.abs(touch.clientX - touchState.current.startX);
      const deltaY = Math.abs(touch.clientY - touchState.current.startY);

      // Determine if this is a horizontal swipe (not vertical scroll)
      // Only mark as swiping if horizontal movement is greater than vertical
      if (deltaX > deltaY && deltaX > 10) {
        touchState.current.isSwiping = true;
        // Prevent default to stop vertical scrolling during horizontal swipe
        e.preventDefault();
      }
    },
    [enabled]
  );

  const handleTouchEnd = useCallback(() => {
    if (!enabled) return;
    if (!touchState.current.startTime || !touchState.current.isSwiping) {
      // Reset state
      touchState.current.isSwiping = false;
      touchState.current.startTime = 0;
      return;
    }

    const deltaX = touchState.current.currentX - touchState.current.startX;
    const deltaY = Math.abs(
      touchState.current.currentY - touchState.current.startY
    );
    const distance = Math.abs(deltaX);
    const duration = Date.now() - touchState.current.startTime;
    const velocity = distance / duration; // px/ms

    // Check if vertical movement is too large (likely a scroll, not a swipe)
    if (deltaY > distance) {
      touchState.current.isSwiping = false;
      touchState.current.startTime = 0;
      return;
    }

    // Calculate distance threshold in pixels
    const screenWidth = window.innerWidth;
    const thresholdDistance = screenWidth * distanceThreshold;

    // Determine if swipe should trigger navigation
    // Fast swipe: velocity > minVelocity and distance > minDistance
    // Slow swipe: distance > thresholdDistance
    const isFastSwipe = velocity > minVelocity && distance > minDistance;
    const isSlowSwipe = distance > thresholdDistance;

    if (isFastSwipe || isSlowSwipe) {
      // Swipe left (next page)
      if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
      // Swipe right (previous page)
      else if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
      }
    }

    // Reset state
    touchState.current.isSwiping = false;
    touchState.current.startTime = 0;
  }, [
    enabled,
    minDistance,
    minVelocity,
    distanceThreshold,
    onSwipeLeft,
    onSwipeRight,
  ]);

  useEffect(() => {
    if (!enabled) return;

    // Only attach listeners on touch devices
    if (!('ontouchstart' in window)) return;

    const targetElement = element || document.body;

    targetElement.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    targetElement.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    targetElement.addEventListener('touchend', handleTouchEnd, {
      passive: true,
    });

    return () => {
      targetElement.removeEventListener('touchstart', handleTouchStart);
      targetElement.removeEventListener('touchmove', handleTouchMove);
      targetElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, element, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    isSwiping: touchState.current.isSwiping,
  };
}
